import React, { useState, useEffect, useCallback } from "react";
import { Store } from "redux";
import { persistStore, Persistor } from "redux-persist";
import {
  AccessibilityToolbar,
  type AccessibilityToolbarLanguageId,
} from "accessibility-toolbar";
import "accessibility-toolbar/styles.css";

import App from "./App";
import Providers from "src/providers";
import store from "#store";
import { addIcon } from "#rscg/Icon";
import { iconNames, styleProperties } from "#constants";
import { setLanguageAction } from "#actionCreators";
import { languageSelector } from "#selectors";

import { AppState } from "#store/types";
import { initializeStyles, setStyleProperties } from "#rsu/styles";
import { useAppDispatch, useAppSelector } from "src/hooks/reactRedux";

type AppLanguage = "en" | "np";

const appLanguageToToolbarLanguage = (
  language: string
): AccessibilityToolbarLanguageId => (language === "np" ? "ne" : "en");

const toolbarLanguageToAppLanguage = (
  languageId: AccessibilityToolbarLanguageId
): AppLanguage => (languageId === "ne" ? "np" : "en");

const SyncedAccessibilityToolbar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { language } = useAppSelector(languageSelector);
  const toolbarLanguage = appLanguageToToolbarLanguage(language);

  const handleToolbarLanguageChange = useCallback(
    (nextLanguageId: AccessibilityToolbarLanguageId) => {
      const nextLanguage = toolbarLanguageToAppLanguage(nextLanguageId);

      if (nextLanguage !== language) {
        dispatch(setLanguageAction({ language: nextLanguage }));
      }
    },
    [dispatch, language]
  );

  return (
    <AccessibilityToolbar
      initialLanguageId={toolbarLanguage}
      languageId={toolbarLanguage}
      position="bottom-left"
      onLanguageChange={handleToolbarLanguageChange}
    />
  );
};

const Root: React.FC = () => {
  const [rehydrated, setRehydrated] = useState(false);
  const [persistor, setPersistor] = useState<Persistor | null>(null);

  // Initialize styles and icons
  useEffect(() => {
    initializeStyles();
    setStyleProperties(styleProperties);

    // Add icons
    Object.keys(iconNames).forEach((key) => {
      addIcon("font", key, iconNames[key as keyof typeof iconNames]);
    });
  }, []);

  // Initialize Redux persist
  useEffect(() => {
    const newPersistor: Persistor = persistStore(
      store as Store<AppState>,
      undefined,
      () => {
        setRehydrated(true);
      }
    );
    setPersistor(newPersistor);

    // Cleanup
    return () => {
      newPersistor.purge();
    };
  }, []);

  // Remove preload element after rehydration
  useEffect(() => {
    if (rehydrated && persistor) {
      const preload = document.getElementById("root-preload");
      if (preload) {
        preload.remove();
      }
    }
  }, [rehydrated, persistor]);

  if (!rehydrated || !persistor) {
    return <div />;
  }

  return (
    <Providers store={store as Store<AppState>} persistor={persistor}>
      <SyncedAccessibilityToolbar />
      <App />
    </Providers>
  );
};

export default Root;
